/**
 * Bulk guide import — the progress dashboard for one batch.
 *
 * Polls the summary (counts + running cost) every few seconds and the job rows for the current
 * status filter and page. It stops polling the moment the batch is terminal — nothing queued or
 * running — so a finished batch is not re-fetched forever. A failed or blocked row can be retried
 * from its archived source; a done row links to its guide in the editor; the whole batch can be
 * cancelled.
 *
 * The worker carries no explicit "paused" flag, so a stalled queue (jobs waiting, nothing running)
 * is inferred over a few polls and surfaced as a may-be-paused banner rather than asserted.
 */
import ConfirmDialog from '@components/Common/ConfirmDialog';
import CRMStatusChip from '@components/Common/CRMStatusChip';
import RecordLink from '@components/Common/RecordLink';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import MintlyTable from '@components/Common/Table';
import { PageHero } from '@components/Common/Panel';
import { useToast } from '@components/Common/Toast/ToastProvider';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import PauseCircleOutlineRoundedIcon from '@mui/icons-material/PauseCircleOutlineRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import { Alert, Box, Button, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  type ImportJob,
  RETRYABLE_JOB_STATUSES,
  cancelBatch,
  getBatchJobs,
  getBatchSummary,
  retryBodyFor,
  retryJob,
  type JobStatus,
} from './importApi';
import {
  type StatusCountMap,
  emptyStatusMap,
  formatCost,
  isTerminal,
  mapFromSummary,
  nextIdlePolls,
  totalCount,
  workerActivity,
} from './batchSummary';
import { statusChipKey, statusLabel } from './jobStatusChip';
import StatusSummary from './StatusSummary';
import './import.scss';

/** How often the dashboard re-reads the batch while it is still moving. */
export const POLL_MS = 4000;
const JOBS_PER_PAGE = 20;

const JOB_COLUMNS = ['fileName', 'status', 'cost', 'detail', 'actions'];

const BatchDashboard: React.FC = () => {
  const { batchId = '' } = useParams<{ batchId: string }>();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [counts, setCounts] = useState<StatusCountMap>(emptyStatusMap());
  const [cost, setCost] = useState(0);
  const [jobs, setJobs] = useState<ImportJob[]>([]);
  const [jobTotal, setJobTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [terminal, setTerminal] = useState(false);

  const [idlePolls, setIdlePolls] = useState(0);
  const idlePollsRef = useRef(0);
  const countsRef = useRef<StatusCountMap>(emptyStatusMap());

  const [cancelOpen, setCancelOpen] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [retrying, setRetrying] = useState<Set<string>>(new Set());

  /* ── Summary (counts + cost) ──────────────────────────────────────────── */

  const loadSummary = useCallback(async () => {
    const summary = await getBatchSummary(batchId);
    const map = mapFromSummary(summary);
    const polls = nextIdlePolls(countsRef.current, map, idlePollsRef.current);
    idlePollsRef.current = polls;
    countsRef.current = map;
    setIdlePolls(polls);
    setCounts(map);
    setCost(Number(summary.totalCostUsd) || 0);
    setTerminal(isTerminal(map));
    return map;
  }, [batchId]);

  /* ── Jobs (the rows for the current filter + page) ────────────────────── */

  const loadJobs = useCallback(async () => {
    const result = await getBatchJobs(batchId, {
      status: statusFilter || undefined,
      page,
      size: JOBS_PER_PAGE,
    });
    setJobs(Array.isArray(result.content) ? result.content : []);
    setJobTotal(Number(result.totalElements) || 0);
  }, [batchId, statusFilter, page]);

  /* First load and the reload-on-filter-change. */
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    Promise.all([loadSummary(), loadJobs()])
      .catch((e: any) => {
        if (!cancelled) setLoadError(e?.response?.data?.error || 'Failed to load this batch');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [loadSummary, loadJobs]);

  /* Polling: re-read until the batch is terminal, then stop. */
  useEffect(() => {
    if (terminal || loadError) return undefined;
    const t = setInterval(() => {
      // Both, so the cost and the visible rows move together; failures here are transient and the
      // next tick retries, so they are swallowed rather than toasted on a loop.
      Promise.all([loadSummary(), loadJobs()]).catch(() => {});
    }, POLL_MS);
    return () => clearInterval(t);
  }, [terminal, loadError, loadSummary, loadJobs]);

  const refreshNow = useCallback(() => {
    Promise.all([loadSummary(), loadJobs()]).catch(() => {});
  }, [loadSummary, loadJobs]);

  /* ── Actions ──────────────────────────────────────────────────────────── */

  const onRetry = async (job: ImportJob) => {
    setRetrying((prev) => new Set(prev).add(job._id));
    try {
      await retryJob(job._id, retryBodyFor(job));
      showToast(`Re-queued ${job.fileName}. It will run again in the background.`, 'info');
      setTerminal(false); // a retry puts work back on the queue, so polling resumes
      idlePollsRef.current = 0;
      setIdlePolls(0);
      refreshNow();
    } catch (e: any) {
      showToast(e?.response?.data?.error || 'Could not retry this file', 'error');
    } finally {
      setRetrying((prev) => {
        const next = new Set(prev);
        next.delete(job._id);
        return next;
      });
    }
  };

  const onCancel = async () => {
    setCanceling(true);
    try {
      const result = await cancelBatch(batchId);
      showToast(`Cancelled ${result.canceled} queued file(s).`, 'success');
      setCancelOpen(false);
      refreshNow();
    } catch (e: any) {
      showToast(e?.response?.data?.error || 'Could not cancel this batch', 'error');
    } finally {
      setCanceling(false);
    }
  };

  const onSelectStatus = (status: string) => {
    setStatusFilter(status);
    setPage(0);
  };

  const total = totalCount(counts);
  const activity = workerActivity(counts, idlePolls);
  const canCancel = !terminal && (counts.queued > 0 || counts.running > 0);

  return (
    <Box className="cc-create guide-import-dashboard">
      <PageHero
        kicker="Guides · bulk import"
        title={`Batch ${batchId}`}
        text={
          terminal
            ? 'This batch has finished. Review the drafts and publish the ones that pass.'
            : 'Generation is running in the background. This page updates on its own.'
        }
        actions={
          <>
            <Button variant="outlined" startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate('/guides/import/batches')}>
              All batches
            </Button>
            <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={refreshNow}>
              Refresh
            </Button>
            {canCancel && (
              <Button variant="outlined" color="error" onClick={() => setCancelOpen(true)}>
                Cancel batch
              </Button>
            )}
          </>
        }
      />

      {loadError ? (
        <Alert severity="error" action={<Button size="small" onClick={refreshNow}>Try again</Button>}>
          {loadError}
        </Alert>
      ) : loading ? (
        <SkeletonLoader rows={10} columns={JOB_COLUMNS.length} />
      ) : (
        <>
          {activity === 'paused' && (
            <Alert className="guide-import-dashboard__paused" severity="warning" icon={<PauseCircleOutlineRoundedIcon />}>
              <strong>Paused.</strong> {counts.queued} file(s) are queued but nothing is running.
              This usually means the OpenAI budget cap was reached. Queued files resume on their own
              once the cap resets; you can also retry a file later.
            </Alert>
          )}

          <Box className="guide-import-dashboard__meters">
            <Box className="cc-panel cc-panel--stat">
              <Typography className="guide-import-dashboard__stat-value">{total}</Typography>
              <Typography className="guide-import-dashboard__stat-label">files</Typography>
            </Box>
            <Box className="cc-panel cc-panel--stat">
              <Typography className="guide-import-dashboard__stat-value">{formatCost(cost)}</Typography>
              <Typography className="guide-import-dashboard__stat-label">cost so far</Typography>
            </Box>
            <Box className="cc-panel cc-panel--stat">
              <Typography className="guide-import-dashboard__stat-value">
                {activity === 'running' ? 'Running' : activity === 'paused' ? 'Paused' : activity === 'waiting' ? 'Waiting' : 'Done'}
              </Typography>
              <Typography className="guide-import-dashboard__stat-label">
                {terminal ? 'batch finished' : 'live'}
              </Typography>
            </Box>
          </Box>

          <StatusSummary counts={counts} active={statusFilter} onSelect={onSelectStatus} />

          {jobs.length === 0 ? (
            <Box className="guide-import-dashboard__empty">
              <Typography color="text.secondary" variant="body2">
                {statusFilter ? `No ${statusLabel(statusFilter).toLowerCase()} files.` : 'No files in this batch yet.'}
              </Typography>
            </Box>
          ) : (
            <MintlyTable
              rows={jobs.map((j) => ({ ...j, id: j._id }))}
              selectors={JOB_COLUMNS}
              pagination={{
                enabled: jobTotal > JOBS_PER_PAGE,
                page,
                rowsPerPage: JOBS_PER_PAGE,
                totalNumberOfRows: jobTotal,
                handleChangePage: (_e: unknown, p: number) => setPage(p),
              }}
              tableHeadRow={{
                fileName: 'File',
                status: 'Status',
                cost: 'Cost',
                detail: 'Detail',
                actions: 'Actions',
              }}
              tableBodyRow={{
                fileName: (j: ImportJob) => (
                  <Tooltip title={j.fileName} placement="top">
                    <Typography noWrap className="guide-import-dashboard__file">{j.fileName}</Typography>
                  </Tooltip>
                ),
                status: (j: ImportJob) => <CRMStatusChip status={statusChipKey(j.status)} label={statusLabel(j.status)} />,
                cost: (j: ImportJob) => <>{j.usage?.costUsd != null ? formatCost(j.usage.costUsd) : '-'}</>,
                detail: (j: ImportJob) => <JobDetail job={j} />,
                actions: (j: ImportJob) => (
                  <Box className="guide-import-dashboard__actions">
                    {j.status === 'done' && j.guideId && (
                      <RecordLink to={`/guides/${j.guideId}`}>Open guide</RecordLink>
                    )}
                    {RETRYABLE_JOB_STATUSES.includes(j.status as JobStatus) && (
                      retrying.has(j._id) ? (
                        <CircularProgress size={16} />
                      ) : (
                        <Tooltip title="Retry this file">
                          <IconButton size="small" aria-label={`Retry ${j.fileName}`} onClick={() => onRetry(j)}>
                            <ReplayOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )
                    )}
                  </Box>
                ),
              }}
            />
          )}
        </>
      )}

      <ConfirmDialog
        open={cancelOpen}
        title="Cancel this batch?"
        body={
          <>
            Queued files that have not started are cancelled. Files already running finish, and files
            already done stay as drafts. This cannot be undone, but you can import the cancelled files
            again later.
          </>
        }
        actionLabel="Cancel batch"
        actionColor="error"
        busy={canceling}
        onConfirm={onCancel}
        onCancel={() => setCancelOpen(false)}
      />
    </Box>
  );
};

/** The per-row detail: the error for a failed row, the model's warnings otherwise. */
const JobDetail: React.FC<{ job: ImportJob }> = ({ job }) => {
  if (job.error && (job.status === 'failed' || job.status === 'blocked')) {
    return <Typography className="guide-import-dashboard__detail is-error">{job.error}</Typography>;
  }
  const warnings = job.warnings;
  const parts: string[] = [];
  if (warnings?.uncoveredPoints?.length) parts.push(`${warnings.uncoveredPoints.length} uncovered point(s)`);
  if (warnings?.imageErrors?.length) parts.push(`${warnings.imageErrors.length} image issue(s)`);
  if (warnings?.notes?.length) parts.push(`${warnings.notes.length} note(s)`);
  if (parts.length === 0) {
    if (job.savedSlug) return <Typography className="guide-import-dashboard__detail">/{job.savedSlug}</Typography>;
    return <>-</>;
  }
  const tooltip = [
    ...(warnings?.uncoveredPoints ?? []),
    ...(warnings?.imageErrors ?? []),
    ...(warnings?.notes ?? []),
  ].join('\n');
  return (
    <Tooltip title={<span className="guide-import-dashboard__tip">{tooltip}</span>}>
      <Typography className="guide-import-dashboard__detail is-warn">{parts.join(', ')}</Typography>
    </Tooltip>
  );
};

export default BatchDashboard;
